import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";
import ScaleIcon from "@mui/icons-material/Scale";

const Keypad = ({
  value: propValue,
  onChange,
  onSubmit,
  onClear,
  cursorPos: externalCursorPos,
  onCursorChange,
  disabled = false,
}) => {
  const [internalValue, setInternalValue] = useState(String(propValue ?? "0"));
  const [cursorPos, setCursorPos] = useState(
    String(propValue ?? "0").length
  );
  const [accumulator, setAccumulator] = useState(null);
  const [operator, setOperator] = useState(null);
  const [resetNext, setResetNext] = useState(true);

  // Buffer for scale value
  const [scaleValue, setScaleValue] = useState(0);
  const lastWeightRef = useRef(null);
  const displayAreaRef = useRef(null);
  const measureTextRef = useRef(null);

  const effectiveCursorPos =
    typeof externalCursorPos === "number" ? externalCursorPos : cursorPos;

  const updateCursorPos = (nextPos) => {
    const normalized = Math.max(0, nextPos);
    if (typeof externalCursorPos !== "number") {
      setCursorPos(normalized);
    }
    onCursorChange?.(normalized);
  };

  // ---------- Sync prop -> state ----------
  useEffect(() => {
    const nextValue = clampToDisplay(normalizeValue(propValue));
    if (nextValue === internalValue) return;
    setInternalValue(nextValue);
    if (typeof externalCursorPos !== "number") {
      setCursorPos(nextValue.length);
    }
  }, [propValue, internalValue, externalCursorPos]);

  // ---------- WebSocket for Scale ----------
  useEffect(() => {
    const socket = new WebSocket("ws://localhost:3001/ws");
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.weight != null) {
          const raw = Number(data.weight);
          const rounded = Number(raw.toFixed(2));
          lastWeightRef.current = rounded;
          setScaleValue(rounded);
        }
      } catch { }
    };
    return () => socket.close();
  }, []);

  const normalizeValue = (v) => {
    if (v == null) return "0";
    if (typeof v === "string") return v;
    return String(v);
  };

  const clampToDisplay = (v) => {
    if (v === "" || v === ".") return "0.";
    return v;
  };

  const sanitizeDecimalInput = (raw) => {
    if (raw == null) return "0";
    let next = String(raw).replace(/[^\d.]/g, "");
    if (next === "") return "0";
    if (/^0+$/.test(next)) return "0";
    const parts = next.split(".");
    if (parts.length > 2) {
      next = parts[0] + "." + parts.slice(1).join("");
    }
    const [intPart, decPart] = next.split(".");
    if (decPart !== undefined && decPart.length > 2) {
      next = `${intPart}.${decPart.slice(0, 2)}`;
    }
    if (/^0+\d/.test(next)) {
      next = next.replace(/^0+/, "");
    }
    if (next === "") return "0";
    if (next.startsWith(".")) return `0${next}`;
    return next;
  };

  const syncCursorFromPoint = (clientX) => {
    const el = displayAreaRef.current;
    const textLength = curForDisplay.length;
    if (!el || textLength <= 0) {
      updateCursorPos(0);
      return;
    }

    const rect = el.getBoundingClientRect();
    const paddingLeft = 4;
    const paddingRight = 4;
    const textWidth =
      measureTextRef.current?.getBoundingClientRect?.().width ??
      Math.max(1, textLength * 18);
    const textRight = rect.right - paddingRight;
    const textLeft = Math.max(rect.left + paddingLeft, textRight - textWidth);
    const charWidth = Math.max(1, textWidth / textLength);
    const x = clientX;

    if (x <= textLeft) {
      updateCursorPos(0);
      return;
    }
    if (x >= textRight) {
      updateCursorPos(textLength);
      return;
    }

    const nextPos = Math.round((x - textLeft) / charWidth);
    updateCursorPos(Math.max(0, Math.min(textLength, nextPos)));
  };

  const handleChange = (newValue) => {
    const nextValue = clampToDisplay(normalizeValue(newValue));
    setInternalValue(nextValue);
    onChange && onChange(nextValue);
  };

  const calculate = (a, op, b) => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);
    if (isNaN(numA) || isNaN(numB)) return b;
    switch (op) {
      case "+":
        return String(numA + numB);
      case "-":
        return String(numA - numB);
      case "*":
        return String(numA * numB);
      default:
        return b;
    }
  };

  // Logic: Handle button clicks
  const handleClick = (val) => {
    if (disabled) return;
    let cur = internalValue;
    const selectionStart = effectiveCursorPos;
    const selectionEnd = effectiveCursorPos;

    const commit = (nextValue, nextCursor = null) => {
      const normalized = clampToDisplay(normalizeValue(nextValue));
      handleChange(normalized);
      updateCursorPos(
        Math.max(
          0,
          Math.min(nextCursor ?? normalized.length, normalized.length)
        )
      );
    };

    if (resetNext && /^[0-9.]$/.test(val)) {
      cur = "0";
      setResetNext(false);
    }

    if (val === "ล้าง") {
      setAccumulator(null);
      setOperator(null);
      setResetNext(true);
      commit("0", 1);
      onClear && onClear();
      return;
    }

    if (["+", "-", "*"].includes(val)) {
      const current = cur || "0";
      if (accumulator !== null && operator && !resetNext) {
        // Chain calculation: 1 + 2 + -> (3) +
        const result = calculate(accumulator, operator, current);
        setAccumulator(result);
        commit(result); // Show intermediate result? Or keep current? Usually keep current until next input. 
        // Actually, calculator shows result.
      } else {
        setAccumulator(current);
      }
      setOperator(val);
      setResetNext(true);
      return;
    }

    if (val === "%") {
      const current = parseFloat(cur || "0");
      const result = String(current / 100);
      commit(result);
      setResetNext(true);
      return;
    }

    if (val === "OK") {
      const current = cur || "0";
      if (accumulator !== null && operator) {
        const result = calculate(accumulator, operator, current);
        commit(result);
        onSubmit && onSubmit(result);
      } else {
        onSubmit && onSubmit(current);
      }
      setAccumulator(null);
      setOperator(null);
      setResetNext(true);
      return;
    }

    if (val === ".") {
      if (cur.includes(".")) return;
      if (selectionStart !== selectionEnd) {
        const next = `${cur.slice(0, selectionStart)}.${cur.slice(selectionEnd)}`;
        if ((next.match(/\./g) || []).length > 1) return;
        commit(next, selectionStart + 1);
        return;
      }
      commit(cur + ".", cur.length + 1);
      return;
    }

    if (val === "SCALE") {
      if (scaleValue !== 0) {
        commit(String(scaleValue));
        setResetNext(true); // Treat scale reading as a new input
      }
      return;
    }

    // Number Logic
    const hasSelection = selectionStart !== selectionEnd;
    const nextBase =
      cur === "0" && !hasSelection && selectionStart === 0 && val !== "."
        ? ""
        : cur;
    const inserted =
      nextBase.slice(0, selectionStart) +
      String(val) +
      nextBase.slice(selectionEnd);

    const sanitized = sanitizeDecimalInput(inserted);
    if (sanitized === null) return;
    const nextCursor = Math.min(selectionStart + 1, sanitized.length);
    commit(sanitized, nextCursor);
  };

  const renderButton = (label, val, options = {}) => {
    const isBackspace = val === "ล้าง";
    const isOperator = ["+", "-", "*", "%"].includes(val);
    const isOK = val === "OK";
    const isScale = val === "SCALE";

    let bg = "#fff";
    let color = "#334155"; // Slate 700
    let hoverBg = "#f8fafc";

    if (isBackspace) {
      bg = "#fecaca"; // Red 200
      color = "#dc2626"; // Red 600
      hoverBg = "#fee2e2";
    }
    else if (isOperator) {
      bg = "#dbeafe"; // Blue 100
      color = "#2563eb"; // Blue 600
      hoverBg = "#bfdbfe";
    }
    else if (isOK) {
      bg = "#3b82f6"; // Blue 500
      color = "#ffffff";
      hoverBg = "#2563eb";
    }
    else if (isScale) {
      bg = "#10b981"; // Emerald 500 (Green)
      color = "#ffffff";
      hoverBg = "#059669"; // Emerald 600
    }

    return (
      <Button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleClick(val)}
        disabled={disabled}
        variant="contained"
        sx={{
          bgcolor: bg,
          color: color,
          fontSize: isOK || isScale ? "1.25rem" : "1.5rem",
          fontWeight: "bold",
          borderRadius: "12px",
          boxShadow: "0 2px 4px -1px rgb(0 0 0 / 0.1)",
          textTransform: "none",
          minWidth: 0,
          height: "100%",
          width: "100%",
          gridColumn: options.gridColumn,
          gridRow: options.gridRow,
          "&:hover": {
            bgcolor: hoverBg,
            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(1px)",
            boxShadow: "none",
          },
          transition: "all 0.1s ease",
          ...options.sx
        }}
      >
        {label}
      </Button>
    );
  };

  const curForDisplay = clampToDisplay(internalValue);
  const safeCursorPos = Math.max(
    0,
    Math.min(effectiveCursorPos, curForDisplay.length)
  );
  const displayBeforeCursor = curForDisplay.slice(0, safeCursorPos);
  const displayAfterCursor = curForDisplay.slice(safeCursorPos);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 360,
        p: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%"
      }}
    >
      {/* Display Screen */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: "#fff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          p: 2,
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          boxShadow: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)"
        }}
      >
        <Box
          ref={measureTextRef}
          sx={{
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            whiteSpace: "pre",
            fontFamily: "monospace",
            fontWeight: "bold",
            letterSpacing: "-1px",
            fontSize: "2rem",
            lineHeight: 1,
          }}
        >
          {curForDisplay}
        </Box>
        <Box
          ref={displayAreaRef}
          onMouseDown={(e) => {
            e.preventDefault();
            syncCursorFromPoint(e.clientX);
          }}
          onMouseMove={(e) => {
            if (e.buttons !== 1) return;
            syncCursorFromPoint(e.clientX);
          }}
          sx={{
            width: "100%",
            minHeight: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "2px",
            userSelect: "none",
            cursor: "text",
            px: 0.5,
            position: "relative",
            fontFamily: "monospace",
            fontWeight: "bold",
            color: "#1e293b",
            letterSpacing: "-1px",
            fontSize: "2rem",
            lineHeight: 1,
            "&::after": {
              content: '""',
              display: "block",
              width: "1px",
            },
          }}
        >
          <Box component="span" sx={{ whiteSpace: "pre" }}>
            {displayBeforeCursor || "\u00a0"}
          </Box>
          <Box
            component="span"
            sx={{
              width: "2px",
              height: "1.35em",
              bgcolor: "#2563eb",
              borderRadius: "999px",
              flexShrink: 0,
              boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.12)",
            }}
          />
          <Box component="span" sx={{ whiteSpace: "pre" }}>
            {displayAfterCursor || "\u00a0"}
          </Box>
        </Box>
      </Paper>

      {/* Grid Layout 4 cols x 5 rows */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gridTemplateRows: "repeat(5, 1fr)",
          gap: 1.5,
          flex: 1
        }}
      >
        {/* Row 1 */}
        {renderButton("7", "7")}
        {renderButton("8", "8")}
        {renderButton("9", "9")}
        {renderButton(<BackspaceIcon sx={{ fontSize: 24 }} />, "ล้าง")}

        {/* Row 2 */}
        {renderButton("4", "4")}
        {renderButton("5", "5")}
        {renderButton("6", "6")}
        {/* Split Column for + and - */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {renderButton("+", "+", { sx: { flex: 1, borderRadius: "8px" } })}
          {renderButton("-", "-", { sx: { flex: 1, borderRadius: "8px" } })}
        </Box>

        {/* Row 3 */}
        {renderButton("1", "1")}
        {renderButton("2", "2")}
        {renderButton("3", "3")}
        {/* Split Column for * and % */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {renderButton("x", "*", { sx: { flex: 1, borderRadius: "8px" } })}
          {renderButton("%", "%", { sx: { flex: 1, borderRadius: "8px" } })}
        </Box>

        {/* Row 4 */}
        {renderButton("0", "0", { gridColumn: "1 / 3" })}
        {renderButton(".", ".")}
        {renderButton(
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <ScaleIcon sx={{ fontSize: 24 }} />
            <Typography variant="caption" sx={{ fontSize: 10, fontWeight: "bold", lineHeight: 1 }}>อ่านตาชั่ง</Typography>
          </Box>,
          "SCALE"
        )}

        {/* Row 5: OK Button spans full width */}
        {renderButton("OK", "OK", { gridColumn: "1 / -1" })}
      </Box>
    </Box>
  );
};

export default Keypad;
